import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import {
  INotificationItem,
  NotificationEventType,
  NotificationPayload,
} from '../models/notification.models';
import { descriptorFor } from './notification-event-registry';

/**
 * Deep-links a notification to the surface that raised it, using the route
 * declared for its event type in the notification event registry.
 */
@Injectable({ providedIn: 'root' })
export class NotificationNavigatorService {
  constructor(private router: Router) {}

  /** Deep-link for a raw event type and its payload, shared by the bell and the desktop banner. */
  navigateTo(eventType: NotificationEventType, payload: NotificationPayload): void {
    const route = descriptorFor(eventType)?.route(payload);
    if (!route) return;

    if (route.queryParams) {
      this.router.navigate(route.path, { queryParams: route.queryParams });
      return;
    }
    this.router.navigate(route.path);
  }

  navigate(notification: INotificationItem): void {
    if (!notification.payload) return;

    this.navigateTo(notification.event_type, notification.payload);
  }

  /**
   * The same deep-link as a URL string, for surfaces that take a href rather than
   * navigating themselves — the in-app toast's `redirectTo`. Built from the registry's
   * route declaration so the toast, the banner and the bell cannot diverge.
   */
  urlFor(eventType: NotificationEventType, payload: NotificationPayload): string | null {
    const route = descriptorFor(eventType)?.route(payload);
    if (!route) return null;

    return this.router.serializeUrl(
      this.router.createUrlTree(route.path, route.queryParams ? { queryParams: route.queryParams } : {}),
    );
  }
}
